import { useState, useEffect, useCallback } from "react";
import { Button, Col, Empty, Form, message, Result, Row, Select } from "antd";
import { EditCompanyFeesProps } from "../../utils/commonInterfaces"; 
import { OutPutResult, Spinner } from '@operations/shared';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import helper from "../../utils/helper";
import { getDeployedCompanyFeesApi, getUnDeployedCompanyFeesApi, updateCompanyFees } from "../../utils/apisConsts";

export const EditCompaniesFees = ({ isDeployedCompany }: EditCompanyFeesProps) => {
    const { Option } = Select
    const [form] = Form.useForm();
    const [companies, setCompanies] = useState([])
    const [serviceGroups, setServiceGroups] = useState([])
    const [feesModels, setFeesModels] = useState([])
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
    const [isLoadingCompanyFees, setIsLoadingCompanyFees] = useState(false)
    const [isLoadingServicesFees, setIsLoadingServicesFees] = useState(false)
    const [isUpdatingCompanyFees, setIsUpdatingCompanyFees] = useState(false)
    const [isError, setIsError] = useState(false)
    const [companyFees, setCompanyFees] = useState<any[]>([{}])
    const [selectedCompanyId, setSelectedCompanyId] = useState<number>()

    const getCompaniesData = useCallback(async () => {
        setIsLoadingCompanies(true)
        setIsError(false)
        const companiesRes: any = await helper.getAllCompanies(isDeployedCompany)
        if (companiesRes.status === 200) {
            setCompanies(companiesRes?.data)
            setIsLoadingCompanies(false)
        } else {
            setIsError(true)
            setIsLoadingCompanies(false)
        }
    }, [isDeployedCompany])
    const getAllServicesAndFees = async () => {
        setIsError(false)
        setIsLoadingServicesFees(true)
        try {
            const [serviceGroups, fees]: any[] = await Promise.all([
                helper.callApiRequest(`/fees/getServiceGroup`, 'GET'),
                helper.callApiRequest(`/fees/getModelsFeesDetails`, 'GET')])
            if (serviceGroups?.data?.status === 200) setServiceGroups(serviceGroups?.data?.data);
            if (fees?.data?.status === 200) setFeesModels(fees?.data?.data);
            setIsLoadingServicesFees(false)
        } catch (err) {
            setIsError(true)
            setIsLoadingServicesFees(false)
        }
    }
    useEffect(() => {
        setSelectedCompanyId(0)
        setCompanyFees([{}])
        setIsError(false)
        setIsLoadingCompanyFees(false)
        getCompaniesData()
        getAllServicesAndFees()
    }, [getCompaniesData])
    const refreshPage = () => {
        window.location.reload();
    }
    const handleSelectCompany = async (id: number) => {
        setSelectedCompanyId(id)
        setIsError(false)
        if (id) {
            setIsLoadingCompanyFees(true)
            try {
                const response: any = await helper.callApiRequest(`${isDeployedCompany ? getDeployedCompanyFeesApi : getUnDeployedCompanyFeesApi}${id}`, 'GET');
                if (response?.status === 200) {
                    setCompanyFees(!response?.data?.company?.companyFees?.length ? [{}] : response?.data?.company?.companyFees)
                    setIsLoadingCompanyFees(false)
                    form.setFieldsValue({ companyFees: !response?.data?.company?.companyFees?.length ? [] : response?.data?.company?.companyFees })
                }
            } catch (err) {
                setIsError(true)
                setIsLoadingCompanyFees(false)
            }
        } else {
            setCompanyFees([{}])
            form.setFieldsValue({ companyFees: [] })
        }
    }
    const handleSubmit = async (values: any) => {
        const body: any = { companyID: values.companyID, companyFees: values.companyFees.map(({ serviceGroupID, feesRateModelID }: { feesRateModelID: number, serviceGroupID: number }) => { return { serviceGroupID, feesRateModelID } }) }
        setIsUpdatingCompanyFees(true)
        try {
            const response: any = await helper.callApiRequest(updateCompanyFees, "PATCH", "", body)
            setIsUpdatingCompanyFees(false)
            if (response?.data.status === 200) {
                message.success("Update company fees successfully")
            } else message.error(response?.data?.message)
        } catch (error: any) {
            setIsUpdatingCompanyFees(false)
            if (error?.data?.status === 500) message.error("Something went wrong")
            else message.error(error?.data?.message)
        }
    }

    return (
        <div className='companyAssign'>
            {isLoadingCompanies ? <Spinner size='large' /> : isError ? <OutPutResult status={'500'} title={'Oops!'} subTitle={'Something went wrong, please refresh and try again'}
                btnTitle={'Refresh the page'} btnClick={refreshPage} /> :
                <Form
                    layout='vertical'
                    form={form}
                    autoComplete="off"
                    onFinish={handleSubmit}
                >
                    <Row>
                        <Col span={24}>
                            <label htmlFor="">Company</label>
                            <Form.Item name="companyID" rules={[{ required: true, message: 'Required' }]}>
                                <Select style={{ width: '100%' }}
                                    placeholder='Please select the company'
                                    showSearch
                                    allowClear
                                    onChange={handleSelectCompany}
                                    filterOption={(input, option: any) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                >
                                    {companies?.map((company: any, key: number) => {
                                        return <Option value={company.id} key={key}>{company.name}</Option>
                                    })}
                                </Select>
                            </Form.Item>
                            {(!isLoadingCompanyFees && !Object.keys(companyFees[0]).length) && !selectedCompanyId &&
                                <Result
                                    title="Operation Portal"
                                    subTitle="Please select the company to view its service groups and fees models"
                                />
                            }
                        </Col>
                    </Row>
                    {isLoadingCompanyFees ? <Spinner size='large' /> : isError ? <OutPutResult status={'500'} title={'Oops!'} subTitle={'Something went wrong, please refresh and try again'}
                        btnTitle={'Refresh the page'} btnClick={refreshPage} /> :
                        <>
                            <Form.List name="companyFees">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => {
                                            return <>
                                                <Row>
                                                    <Col span={12} key={key}>
                                                        <label htmlFor="">Service group</label>
                                                        <Form.Item {...restField} name={[name, `serviceGroupID`]} rules={[{ required: true, message: 'Required' }]}>
                                                            <Select style={{ width: '100%' }} placeholder='Please select the group service' disabled={isLoadingServicesFees} >
                                                                {serviceGroups?.map((group: any, key: number) => {
                                                                    return <Option value={group.id} services={group.services} key={key}>{group.name}</Option>
                                                                })}
                                                            </Select>

                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={11}>
                                                        <label htmlFor="">Fees Models</label>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'feesRateModelID']}
                                                            rules={[{ required: true, message: 'Required' }]}
                                                        >
                                                            <Select
                                                                placeholder="Please select the services"
                                                                optionFilterProp="children"
                                                                disabled={isLoadingServicesFees}
                                                                filterOption={(input: string, option: any) =>
                                                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                                }>
                                                                {feesModels?.map((model: any, key: number) => {
                                                                    return <Option value={model.id} key={key}>{model.name}</Option>
                                                                })}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={1}>
                                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ position: 'relative', top: '27px', padding: '0 0px' }} />
                                                    </Col>
                                                </Row>
                                            </>
                                        })}
                                        {selectedCompanyId && !fields.length ? <Empty description='There is no fees or service groups but you can add yours' style={{ margin: "1rem 0" }} /> : null}
                                        {selectedCompanyId ? <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                Add
                                            </Button>
                                        </Form.Item> : null}
                                        {selectedCompanyId && (Object.keys(companyFees[0]).length || fields.length) ?
                                            <Form.Item>
                                                <Button type="primary" htmlType="submit" loading={isUpdatingCompanyFees} style={{ marginTop: '1rem' }}>
                                                    Submit
                                                </Button>
                                            </Form.Item> : null}
                                    </>
                                )}
                            </Form.List>
                        </>
                    }
                </Form>
            }
        </div>
    )
}
